import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import NumberInputField from 'components/inputFields/NumberInputField';
import TextField from 'components/core/TextField';
// Custom
import { errorShape } from 'constants/PropTypesShapes';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import FormItem from 'components/misc/FormItem';
import FormItemsGroup from 'components/misc/FormItemsGroup';
import { buildFormErrors, serviceFeeRuleConstraints } from 'utils/Forms';
import { sanitizeFloatRounded } from 'utils/Tools';

const AmountInputField = ({
  amount, errors, fieldName, onFieldsChange,
}) => (
  <NumberInputField
    margin="none"
    label={<LocalizedMessage id={`field.${fieldName}`} />}
    min={0}
    value={amount}
    sanitizer={sanitizeFloatRounded(2, true)}
    errorText={_get(errors, fieldName)}
    onChange={(v) => onFieldsChange({ [fieldName]: v })}
  />
);

AmountInputField.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  errors: PropTypes.object,
  fieldName: PropTypes.string.isRequired,
  onFieldsChange: PropTypes.func.isRequired,
};

AmountInputField.defaultProps = {
  amount: undefined,
  errors: null,
};

class ServiceFeeRuleAddEdit extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        maximumAmount, minimumAmount, name, priceThreshold, value,
      },
      onErrorsChange,
      onValidate,
    } = this.props;

    const errors = buildFormErrors(
      { name, value },
      serviceFeeRuleConstraints,
    );

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate({
        maximumAmount, minimumAmount, name, priceThreshold, value,
      });
      return true;
    }

    return false;
  }

  render() {
    const {
      fields: {
        maximumAmount, minimumAmount, name, priceThreshold, value,
      },
      errors,
      onFieldsChange,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: (
                      <LocalizedMessage id="help.serviceFeeRules.value" />
                    ),
                  },
                  {
                    content: (
                      <LocalizedMessage id="help.serviceFeeRules.priceThreshold" />
                    ),
                  },
                  {
                    content: (
                      <LocalizedMessage id="help.serviceFeeRules.minimumAmount" />
                    ),
                  },
                  {
                    content: (
                      <LocalizedMessage id="help.serviceFeeRules.maximumAmount" />
                    ),
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                {/* ----- Name ----- */}
                <FormItemsGroup paddings="top">
                  <FormItem>
                    <TextField
                      margin="none"
                      label={<LocalizedMessage id="field.name" />}
                      value={name}
                      required
                      errorText={_get(errors, 'name')}
                      onChange={(v) => onFieldsChange({ name: v })}
                    />
                  </FormItem>
                </FormItemsGroup>
                {/* ----- Value ----- */}
                <FormItemsGroup paddings="top">
                  <FormItem>
                    <NumberInputField
                      margin="none"
                      label={<LocalizedMessage id="field.value" />}
                      min={0}
                      max={100}
                      value={value}
                      required
                      sanitizer={sanitizeFloatRounded(2, true)}
                      errorText={_get(errors, 'value')}
                      adornments={(value && { start: '%' }) || {}}
                      onChange={(v) => onFieldsChange({ value: v })}
                    />
                  </FormItem>
                </FormItemsGroup>
                {/* ----- Price threshold ----- */}
                <FormItemsGroup paddings="top">
                  <FormItem>
                    <AmountInputField
                      amount={priceThreshold}
                      errors={errors}
                      fieldName="priceThreshold"
                      onFieldsChange={onFieldsChange}
                    />
                  </FormItem>
                </FormItemsGroup>
                {/* ----- Maximum and minimum amounts ----- */}
                <FormItemsGroup paddings="top">
                  <FormItem>
                    <AmountInputField
                      amount={minimumAmount}
                      errors={errors}
                      fieldName="minimumAmount"
                      onFieldsChange={onFieldsChange}
                    />
                  </FormItem>
                  <FormItem paddings="top">
                    <AmountInputField
                      amount={maximumAmount}
                      errors={errors}
                      fieldName="maximumAmount"
                      onFieldsChange={onFieldsChange}
                    />
                  </FormItem>
                </FormItemsGroup>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

ServiceFeeRuleAddEdit.propTypes = {
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
};

ServiceFeeRuleAddEdit.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  maximumAmount: {
    resourceKey: 'maximumAmount',
    defaultValue: undefined,
  },
  minimumAmount: {
    resourceKey: 'minimumAmount',
    defaultValue: undefined,
  },
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  priceThreshold: {
    resourceKey: 'priceThreshold',
    defaultValue: undefined,
  },
  value: {
    resourceKey: 'value',
    defaultValue: undefined,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: _get(ownProps, 'serviceFeeRule'),
  operationError: {
    ...ownProps.operationError,
  },
});

const mapStateToProps = (state) => ({
  operationError: state.cpmServiceFeeRule.create.error || state.cpmServiceFeeRule.update.error,
});

module.exports = compose(
  connect(mapStateToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['createServiceFeeRule', 'updateServiceFeeRule']),
)(ServiceFeeRuleAddEdit);



// WEBPACK FOOTER //
// ./routes/cpm/routes/workspaces/assets/serviceFeeRule/ServiceFeeRuleAddEditForm.js